import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Calculator as CalcIcon, ArrowRight, Zap, TrendingUp, Sparkles, Clock, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIB_OPTIONS = ["<6 months", "6-12 months", "1-2 years", "2+ years"];

// Log-scale slider config — mirrors the Step-1 funding slider on the
// apply form so the home-page Calculator feels identical to the form
// the customer is about to land on. Monthly revenue is a 200x range
// ($10K-$2M); linear scale crams all the common values (where 80% of
// applicants sit, $30K-$300K) into the bottom 15% of the track.
const MIN_REVENUE = 10000;
const MAX_REVENUE = 2000000;
const LOG_MIN = Math.log(MIN_REVENUE);
const LOG_MAX = Math.log(MAX_REVENUE);
const SLIDER_STEPS = 1000;

const REVENUE_PRESETS = [25000, 75000, 250000, 500000, 1000000];

function positionToRevenue(pos) {
  const ratio = pos / SLIDER_STEPS;
  const raw = Math.exp(LOG_MIN + ratio * (LOG_MAX - LOG_MIN));
  return Math.round(raw / 1000) * 1000;
}

function revenueToPosition(amount) {
  const safe = Math.min(Math.max(amount, MIN_REVENUE), MAX_REVENUE);
  const ratio = (Math.log(safe) - LOG_MIN) / (LOG_MAX - LOG_MIN);
  return Math.round(ratio * SLIDER_STEPS);
}

const TIER_ICONS = {
  express: Zap,
  short: Clock,
  standard: TrendingUp,
  extended: Flame,
  premium: Sparkles,
};

const TIER_ACCENTS = {
  express: "from-[#0052FF] to-[#00FFA3]",
  short: "from-[#00FFA3] to-[#0052FF]",
  standard: "from-[#0052FF] to-[#00FFA3]",
  extended: "from-[#00FFA3] to-[#0052FF]",
  premium: "from-[#0052FF] to-[#00FFA3]",
};

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtCents = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function Calculator() {
  const [revenue, setRevenue] = useState(50000);
  const [tib, setTib] = useState("1-2 years");
  const [result, setResult] = useState(null);

  // Slider position cached so we don't re-run the log math twice per
  // render (slider value + each preset tick position).
  const sliderPos = useMemo(() => revenueToPosition(revenue), [revenue]);
  const presetPositions = useMemo(
    () => REVENUE_PRESETS.map((amount) => ({
      amount,
      pct: (revenueToPosition(amount) / SLIDER_STEPS) * 100,
    })),
    [],
  );

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/calculator/estimate`, {
          monthly_revenue: revenue,
          time_in_business: tib,
        });
        setResult(data);
      } catch (e) {
        // no-op
      }
    }, 200);
    return () => clearTimeout(t);
  }, [revenue, tib]);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative py-12 md:py-32 border-t border-white/5"
    >
      <div className="hidden md:block absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#0052FF]/10 blur-[120px] pointer-events-none" />
      <div className="hidden md:block absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#00FFA3]/8 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-12 relative">
        {/* Heading */}
        <div className="text-center mb-8 md:mb-12">
          <p className="hidden md:block text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-[#0052FF] mb-4">
            / 06 — Eligibility Engine
          </p>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter uppercase leading-[0.95] mb-3 md:mb-4">
            See your offers in <span className="text-gradient-neon">10 seconds.</span>
          </h2>
          <p className="hidden md:block text-zinc-400 leading-relaxed max-w-2xl mx-auto text-base md:text-lg">
            Adjust your monthly revenue and time in business. Our engine returns multiple real-time pre-approval structures — instantly.
          </p>
          <p className="md:hidden text-zinc-400 leading-relaxed text-sm">
            Adjust your monthly revenue and see real-time offers.
          </p>
          <div className="mt-4 md:mt-5 inline-flex items-center gap-2 text-xs font-mono text-zinc-500 uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-[#00FFA3] animate-pulse" />
            Live underwriting model
          </div>
        </div>

        {/* Inputs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-strong rounded-3xl p-6 md:p-10 border border-white/10 mb-8"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0052FF] to-[#00FFA3] grid place-items-center">
              <CalcIcon className="w-5 h-5 text-black" />
            </div>
            <h3 className="font-display text-xl font-bold">Funding Estimator</h3>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Monthly revenue slider — log-scale + preset chips +
                tick marks, matching the Step-1 funding slider on the
                apply form so the customer feels at home. */}
            <div>
              <div className="flex justify-between items-end mb-4">
                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                  Monthly Revenue
                </label>
                <span className="font-display text-2xl md:text-3xl font-extrabold text-white" data-testid="calc-revenue-display">
                  {fmt(revenue)}
                </span>
              </div>
              <div className="relative">
                <Slider
                  value={[sliderPos]}
                  onValueChange={(v) => setRevenue(positionToRevenue(v[0]))}
                  min={0}
                  max={SLIDER_STEPS}
                  step={1}
                  className="cursor-pointer mb-3"
                  data-testid="calc-revenue-slider"
                />
                {/* Tick marks at each preset — clicking snaps the
                    slider there. Positioned via the same log scale
                    the slider uses. */}
                <div
                  className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none"
                  data-testid="calc-revenue-ticks"
                >
                  {presetPositions.map(({ amount, pct }) => {
                    const active = revenue === amount;
                    return (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setRevenue(amount)}
                        data-testid={`calc-revenue-tick-${amount}`}
                        aria-label={`Set monthly revenue to ${fmt(amount)}`}
                        className="absolute pointer-events-auto -translate-x-1/2 -translate-y-1/2 p-2 -m-2 group"
                        style={{ left: `${pct}%`, top: "50%" }}
                      >
                        <span
                          className={`block w-[2px] h-3 rounded-full transition-all ${
                            active
                              ? "bg-[#00B574] h-4"
                              : "bg-zinc-400 group-hover:bg-[#00B574] group-hover:h-4"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-between text-xs text-zinc-600 font-mono">
                <span>$10K</span>
                <span>$2M+</span>
              </div>
              <div className="grid grid-cols-5 gap-2 mt-5">
                {REVENUE_PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setRevenue(p)}
                    data-testid={`calc-revenue-preset-${p}`}
                    className={`px-2 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      revenue === p
                        ? "bg-[#00FFA3] text-black"
                        : "bg-[#111] text-zinc-300 border border-white/5 hover:border-[#00FFA3]/40"
                    }`}
                  >
                    {p >= 1000000 ? `$${p / 1000000}M` : `$${p / 1000}K`}
                  </button>
                ))}
              </div>
            </div>

            {/* Time in business */}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4 block">
                Time In Business
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TIB_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setTib(opt)}
                    data-testid={`calc-tib-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    className={`px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                      tib === opt
                        ? "bg-[#00FFA3] text-black"
                        : "bg-[#1A1A1A] text-zinc-400 hover:text-white border border-white/5"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tier grid */}
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#0052FF] mb-4 text-center">
            / Pre-approved offer structures
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="calc-tiers">
            {(result?.tiers || []).map((tier, i) => (
              <TierCard key={tier.key} tier={tier} tib={tib} idx={i} />
            ))}
            {!result && Array.from({ length: 5 }).map((_, i) => <TierSkeleton key={i} />)}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-zinc-600 mt-8 max-w-2xl mx-auto">
          Estimates only. Final offers structured by your assigned advisor after a 2-minute application. Soft pull — no FICO impact.
        </p>
      </div>
    </section>
  );
}

function TierCard({ tier, tib, idx }) {
  const Icon = TIER_ICONS[tier.key] || Sparkles;
  const accent = TIER_ACCENTS[tier.key] || "from-[#0052FF] to-[#00FFA3]";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.06 }}
      className={`relative bg-[#111] rounded-2xl p-5 border ${
        tier.recommended ? "border-[#00FFA3]/50 shadow-[0_0_50px_rgba(0,255,163,0.15)]" : "border-white/5"
      } overflow-hidden`}
      data-testid={`calc-tier-${tier.key}`}
    >
      {tier.recommended && (
        <div className="absolute top-0 right-0 px-2.5 py-1 text-[9px] font-mono uppercase tracking-widest bg-[#00FFA3] text-black rounded-bl-lg">
          Top Pick
        </div>
      )}

      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${accent} p-[1px] mb-3`}>
        <div className="w-full h-full rounded-[7px] bg-[#0A0A0A] grid place-items-center">
          <Icon className="w-4 h-4 text-[#00FFA3]" />
        </div>
      </div>

      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{tier.label}</p>
      <p className="text-[10px] font-mono text-[#00FFA3] mb-3">{tier.speed}</p>

      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Pre-approved up to</p>
      <p className="font-display text-3xl font-extrabold text-gradient-neon tracking-tighter mb-3">
        {fmt(tier.amount)}
      </p>

      <div className="space-y-1.5 pt-3 border-t border-white/5">
        <Row k="Term" v={`${tier.term_months} months`} />
        <Row k={`${tier.payment_frequency} pmt`} v={fmtCents(tier.payment_amount)} />
        <Row k="Factor" v={`${tier.factor_rate}x`} />
      </div>

      <Link
        to={`/apply?amount=${tier.amount}&tib=${encodeURIComponent(tib)}&tier=${tier.key}&label=${encodeURIComponent(tier.label)}&term=${tier.term_months}&factor=${tier.factor_rate}&pmt_freq=${tier.payment_frequency}&pmt_amt=${tier.payment_amount}&speed=${encodeURIComponent(tier.speed)}`}
        data-testid={`calc-apply-${tier.key}`}
        className={`mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold transition-all ${
          tier.recommended
            ? "bg-[#00FFA3] text-black hover:bg-white"
            : "bg-[#0A0A0A] text-white border border-white/10 hover:border-[#00FFA3]/40"
        }`}
      >
        Lock In <ArrowRight className="w-3 h-3" />
      </Link>
    </motion.div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-center text-[10px]">
      <span className="text-zinc-500 font-mono uppercase tracking-widest">{k}</span>
      <span className="font-mono font-bold text-white">{v}</span>
    </div>
  );
}

function TierSkeleton() {
  return (
    <div className="bg-[#111] rounded-2xl p-5 border border-white/5 animate-pulse">
      <div className="w-9 h-9 rounded-lg bg-white/5 mb-3" />
      <div className="h-3 bg-white/5 rounded w-2/3 mb-2" />
      <div className="h-2 bg-white/5 rounded w-1/2 mb-4" />
      <div className="h-8 bg-white/5 rounded w-3/4 mb-3" />
      <div className="space-y-1.5 pt-3 border-t border-white/5">
        <div className="h-2 bg-white/5 rounded" />
        <div className="h-2 bg-white/5 rounded" />
        <div className="h-2 bg-white/5 rounded" />
      </div>
    </div>
  );
}
